import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useParams } from "react-router-dom";
import axios from "axios";
import NavbarDumai from "../../Components/Navbar";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";

const API = process.env.REACT_APP_API_BASE_URL;
const STORAGE = process.env.REACT_APP_STORAGE_URL;

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

export {};

interface ProductImage {
  id: number;
  image: string;
}

interface Category {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  price: string;
  discount: number | null;
  final_price: number;
  stock: number;
  description: string;
  images: ProductImage[];
  sizes?: string[];
  colors?: string[];
  category?: Category;
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [activeImage, setActiveImage] = useState<string>("");
  const pixelFired = useRef(false);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const [currency, setCurrency] = useState<"IDR" | "USD" | "MYR">("IDR");
  const [rates, setRates] = useState<any>(null);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await axios.get("https://open.er-api.com/v6/latest/IDR");
        setRates(res.data.rates);
      } catch (err) {
        console.log("Rate error", err);
      }
    };

    fetchRates();
  }, []);

  const handleBuyNow = () => {
    if (window.fbq && product) {
      window.fbq("track", "AddToCart", {
        content_name: product.name,
        content_ids: [product.id],
        content_type: "product",
        value: price,
        currency: "IDR",
      });

      console.log("Pixel AddToCart fired");
    }

    navigate("/checkout", {
      state: {
        product,
        qty,
        size: selectedSize,
        color: selectedColor,
        price,
        ref,
      },
    });
  };

  useEffect(() => {
    axios.get(`${API}/api/produk/${id}`).then((res) => {
      const data = res.data;
      setProduct(data);
      setActiveImage(data.images[0]?.image);
    });
  }, [id]);

  useEffect(() => {
    if (!product) return;

    if (window.fbq && !pixelFired.current) {
      window.fbq("track", "ViewContent", {
        content_name: product.name,
        content_ids: [product.id],
        content_type: "product",
        value: product.final_price ?? product.price,
        currency: "IDR",
      });

      console.log("Pixel ViewContent fired");

      pixelFired.current = true;
    }
  }, [product]);

  if (!product) return <div className="p-10 text-center"></div>;

  const hasDiscount = product.discount && Number(product.discount) > 0;

  const price = hasDiscount ? Number(product.final_price) : Number(product.price);

  const totalPrice = price * qty;

  const getConvertedPrice = () => {
    if (!rates || currency === "IDR") return totalPrice;

    const rate = rates[currency];
    if (!rate) return totalPrice;

    return totalPrice * rate;
  };

  return (
    <>
      <section className="font-signika">
        <NavbarDumai />

        <section className="max-w-7xl mx-auto px-4 pt-36 max-sm:pt-20 pb-10">
          <div className="grid md:grid-cols-2 gap-10">
            {/* LEFT IMAGE */}
            <div>
              <div className="relative bg-gray-100 rounded-xl overflow-hidden">
                {/* DISCOUNT BADGE */}
                {hasDiscount && <div className="absolute top-3 left-3 bg-red-500 text-white text-sm px-3 py-1 rounded-md shadow">-{product.discount}%</div>}

                <img src={`${STORAGE}/products/${activeImage}`} className="w-full object-contain h-[400px] max-sm:h-full" alt={product.name} />
              </div>

              {/* THUMBNAILS */}
              <div className="flex gap-3 mt-4 overflow-x-auto">
                {product.images.map((img) => (
                  <img
                    key={img.id}
                    src={`${STORAGE}/products/${img.image}`}
                    alt=""
                    onClick={() => setActiveImage(img.image)}
                    className={`w-20 h-20 object-cover rounded-lg cursor-pointer border ${activeImage === img.image ? "border-primary" : "border-gray-200"}`}
                  />
                ))}
              </div>
            </div>

            {/* RIGHT INFO */}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{product.name}</h1>

              <p className="text-sm text-gray-500 mt-2">Kategori: {product.category?.name}</p>

              {/* PRICE */}
              <div className="mt-6 space-y-2">
                {hasDiscount && <div className="text-gray-400 line-through text-lg">Rp {Number(product.price).toLocaleString()}</div>}

                {/* PRICE UTAMA */}
                <div className="text-primary text-2xl font-bold">{currency === "IDR" ? `Rp ${price.toLocaleString()}` : `${currency} ${getConvertedPrice().toFixed(2)}`}</div>

                {/* TOTAL */}
                <div className="text-sm text-gray-500">
                  Total: <span className="font-semibold">{currency === "IDR" ? `Rp ${totalPrice.toLocaleString()}` : `${currency} ${getConvertedPrice().toFixed(2)}`}</span>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-1">Currency</p>

                <div className="flex gap-2">
                  {["IDR", "USD", "MYR"].map((cur) => (
                    <button
                      key={cur}
                      onClick={() => setCurrency(cur as any)}
                      className={`px-3 py-1 rounded-lg text-sm border transition
        ${currency === cur ? "bg-primary text-white border-primary" : "border-gray-300 hover:bg-gray-100"}`}>
                      {cur}
                    </button>
                  ))}
                </div>
              </div>

              {/* SIZE */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="mt-6">
                  <p className="font-medium mb-2">Size</p>

                  <div className="flex gap-2 flex-wrap">
                    {product.sizes.map((size) => (
                      <button key={size} onClick={() => setSelectedSize(size)} className={`px-4 py-2 border rounded-lg text-sm ${selectedSize === size ? "bg-primary text-white border-primary" : "border-gray-300"}`}>
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* COLOR */}
              {product.colors && product.colors.length > 0 && (
                <div className="mt-6">
                  <p className="font-medium mb-2">Color</p>

                  <div className="flex gap-2 flex-wrap">
                    {product.colors.map((color) => (
                      <button key={color} onClick={() => setSelectedColor(color)} className={`px-4 py-2 border rounded-lg text-sm ${selectedColor === color ? "bg-primary text-white border-primary" : "border-gray-300"}`}>
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* QTY */}
              <div className="mt-6">
                <p className="font-medium mb-2">Jumlah</p>

                <div className="flex items-center gap-3">
                  <button onClick={() => setQty(qty > 1 ? qty - 1 : 1)} className="w-8 h-8 border rounded">
                    -
                  </button>

                  <span className="w-10 text-center">{qty}</span>

                  <button onClick={() => setQty(qty < product.stock ? qty + 1 : qty)} className="w-8 h-8 border rounded">
                    +
                  </button>

                  <span className="text-sm text-gray-500">{product.stock} tersedia</span>
                </div>
              </div>

              {/* BUTTON */}
              <div className="flex gap-4 mt-8">
                <button onClick={handleBuyNow} className="flex-1 bg-primary text-white py-3 rounded-lg hover:bg-purple-600">
                  Beli Langsung
                </button>

                {/* <button className="flex-1 bg-primary text-white py-3 rounded-lg hover:bg-purple-600">+ Keranjang</button> */}
              </div>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div className="mt-14 bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold mb-4 text-lg">Deskripsi</h3>

            <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
          </div>
        </section>
      </section>
    </>
  );
}
